import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Spinner, Tab, Tabs } from 'react-bootstrap';
import { AmlCase, AmlCaseStatus, DecisionFormState, ProviderHistoryResponse, ScoreFactorDetail, ScoringBreakdown } from './types';
import {
  STATUS_LABELS, STATUS_BADGE,
  RISK_LABELS, RISK_BADGE,
  formatDateTime, formatAmount,
} from './amlUtils';
import AmlScoringPanel from './AmlScoringPanel';
import AmlRules from './AmlRules';
import AmlTimeline from './AmlTimeline';
import AmlUserProfile from './AmlUserProfile';
import AmlDecisionModal from './AmlDecisionModal';
import AmlProviderHistory from './AmlProviderHistory';
import { userSummaryInline } from './formatUser';
import ApiService from '../../helpers/apiService';
import { hasAccess } from '../../helpers/access';

interface Props {
  amlCase: AmlCase;
  onDecisionSubmit: (caseId: string, form: DecisionFormState) => Promise<void>;
  onClose: () => void;
}

// Детальная карточка AML-кейса с вкладками
const AmlCaseDetail: React.FC<Props> = ({ amlCase, onDecisionSubmit, onClose }) => {
  const [activeTab, setActiveTab] = useState('transaction');
  const [showDecisionModal, setShowDecisionModal] = useState(false);

  // Ленивая загрузка истории провайдеров — только при открытии вкладки
  const [providerHistory, setProviderHistory] = useState<ProviderHistoryResponse | null>(null);
  const [providerHistoryLoading, setProviderHistoryLoading] = useState(false);
  const [providerHistoryError, setProviderHistoryError] = useState<string | undefined>();
  const [providerHistoryLoaded, setProviderHistoryLoaded] = useState(false);

  // Сбрасываем при смене кейса
  useEffect(() => {
    setProviderHistory(null);
    setProviderHistoryLoaded(false);
    setProviderHistoryError(undefined);
  }, [amlCase.id]);

  const handleProviderTabEnter = () => {
    if (providerHistoryLoaded) return;
    // Demo-режим: синтетический id "tx-..." не существует в БД, бекенд парсит
    // :id как UUID и вернёт 400. Подсовываем пустой результат.
    if (amlCase.id.startsWith('tx-')) {
      setProviderHistory({ items: [], total: 0, limit: 0, offset: 0 });
      setProviderHistoryLoaded(true);
      return;
    }
    setProviderHistoryLoading(true);
    (ApiService as any).amlGetProviderHistory(amlCase.id)
      .then((data: ProviderHistoryResponse) => { setProviderHistory(data); setProviderHistoryLoaded(true); })
      .catch(() => setProviderHistoryError('Не удалось загрузить историю провайдеров.'))
      .finally(() => setProviderHistoryLoading(false));
  };

  // Вычисляем breakdown скора из истории провайдеров
  const computedBreakdown = useMemo((): ScoringBreakdown => {
    const base = amlCase.scoringBreakdown;
    if (!providerHistory || providerHistory.items.length === 0) return base;

    const marbleItems = providerHistory.items.filter(i => i.source === 'marble');
    const crystalItems = providerHistory.items.filter(i => i.source === 'crystal');

    const maxMarble = marbleItems.length > 0 ? Math.max(...marbleItems.map(i => i.score ?? 0)) : 0;
    const maxCrystal = crystalItems.length > 0 ? Math.max(...crystalItems.map(i => i.score ?? 0)) : 0;

    const details: ScoreFactorDetail[] = [
      ...marbleItems.slice(0, 5).map(item => ({
        factor: 'Marble AML',
        score: item.score ?? 0,
        weight: 0.4,
        description: item.summary,
      })),
      ...crystalItems.slice(0, 5).map(item => ({
        factor: 'Crystal KYT',
        score: item.score ?? 0,
        weight: 0.3,
        description: item.summary,
      })),
    ];

    return {
      total: base.total,
      behaviorScore: maxMarble || base.behaviorScore,
      transactionScore: maxCrystal || base.transactionScore,
      kycScore: base.kycScore,
      geoScore: base.geoScore,
      details,
    };
  }, [amlCase.scoringBreakdown, providerHistory]);

  const canDecide = ![AmlCaseStatus.APPROVED, AmlCaseStatus.REJECTED].includes(amlCase.status);

  // US-PM-05: завести задачу в продуктовый бэклог из AML-кейса.
  const canBacklog = hasAccess('backlogEdit');
  const [backlogState, setBacklogState] = useState<'idle' | 'sending' | 'done' | 'error'>('idle');
  const handleAddToBacklog = async () => {
    setBacklogState('sending');
    const ref = amlCase.caseId || amlCase.id;
    const rules = (amlCase.triggeredRules as any[])
      .map((r) => r?.ruleName || r?.ruleCode || r?.code).filter(Boolean).join(', ');
    const analysis = [
      `**Источник:** AML-кейс ${ref}`,
      `**Риск:** ${RISK_LABELS[amlCase.riskLevel]}`,
      `**Пользователь:** ${userSummaryInline(amlCase)}`,
      `**Транзакция:** ${amlCase.transaction.type} ${formatAmount(amlCase.transaction.amount, amlCase.transaction.currency)}`,
      `**Сработавшие правила:** ${rules || '—'}`,
    ].join('\n\n');
    try {
      await (ApiService as any).backlogAction('create', {
        title: `AML-кейс ${ref}`,
        analysis_md: analysis,
        priority: 'high',
        source: 'aml_case',
        from_ref_id: amlCase.caseId || amlCase.id,
      });
      setBacklogState('done');
    } catch {
      setBacklogState('error');
    }
  };

  const txn = amlCase.transaction;
  const direction = txn.direction;
  const directionLabel = direction === 'in' ? '↓ Входящая' : '↑ Исходящая';
  const directionColor = direction === 'in' ? 'success' : 'danger';

  return (
    <div className="aml-case-detail">
      {/* Заголовок карточки */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
            <span className="fw-bold fs-6">{amlCase.caseId || amlCase.id}</span>
            <Badge bg={STATUS_BADGE[amlCase.status]}>
              {STATUS_LABELS[amlCase.status]}
            </Badge>
            <Badge bg={RISK_BADGE[amlCase.riskLevel]}>
              {RISK_LABELS[amlCase.riskLevel]}
            </Badge>
          </div>
          <div className="text-muted small">
            {userSummaryInline(amlCase)}
          </div>
          <div className="text-muted" style={{ fontSize: '0.78rem' }}>
            Создан: {formatDateTime(amlCase.createdAt)} · Срок: {formatDateTime(amlCase.dueAt)}
          </div>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {canBacklog && (
            <Button
              variant={backlogState === 'done' ? 'success' : 'outline-primary'}
              size="sm"
              disabled={backlogState === 'sending' || backlogState === 'done'}
              onClick={handleAddToBacklog}
              title="Завести задачу в продуктовый бэклог"
            >
              {backlogState === 'sending' && <Spinner animation="border" size="sm" className="me-1" />}
              {backlogState === 'done' ? 'В бэклоге ✓' : backlogState === 'error' ? 'Ошибка — повторить' : 'В бэклог'}
            </Button>
          )}
          {canDecide && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowDecisionModal(true)}
            >
              Принять решение
            </Button>
          )}
          <Button variant="outline-secondary" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>
      </div>

      {/* Если есть решение — отобразить */}
      {amlCase.decision && (
        <div className={`alert alert-${amlCase.decision.decision === 'approve' ? 'success' : 'danger'} py-2 small mb-3`}>
          <strong>Решение:</strong> {amlCase.decision.decision === 'approve' ? 'Одобрено' : 'Отклонено'}{' '}
          — {amlCase.decision.operatorName} ({formatDateTime(amlCase.decision.decidedAt)})
          <div className="text-muted mt-1">{amlCase.decision.comment}</div>
        </div>
      )}

      {/* Вкладки */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || 'transaction')}
        className="mb-3"
      >
        {/* Вкладка 1: Транзакция + Правила */}
        <Tab eventKey="transaction" title="Транзакция">
          <div className="mb-3">
            <div className="row g-2 mb-3">
              <div className="col-12 col-sm-6">
                <div className="text-muted small">ID транзакции</div>
                <code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>{txn.id || '—'}</code>
              </div>
              <div className="col-6 col-sm-3">
                <div className="text-muted small">Сумма</div>
                <strong className={`text-${directionColor}`}>
                  {directionLabel} {formatAmount(txn.amount, txn.currency)}
                </strong>
              </div>
              <div className="col-6 col-sm-3">
                <div className="text-muted small">Тип</div>
                <span>{txn.type}</span>
              </div>
              <div className="col-6 col-sm-3">
                <div className="text-muted small">Статус</div>
                <Badge bg={txn.status === 'blocked' ? 'danger' : txn.status === 'completed' ? 'success' : 'warning'}>
                  {txn.status}
                </Badge>
              </div>
              <div className="col-6 col-sm-3">
                <div className="text-muted small">Дата</div>
                <span>{txn.createdAt ? formatDateTime(txn.createdAt) : '—'}</span>
              </div>
              {(txn as any).blockchain && (
                <div className="col-6 col-sm-3">
                  <div className="text-muted small">Сеть</div>
                  <span>{(txn as any).blockchain}</span>
                </div>
              )}
              {(txn as any).marbleScore != null && (
                <div className="col-6 col-sm-3">
                  <div className="text-muted small">Marble score</div>
                  <span>{(txn as any).marbleScore}</span>
                </div>
              )}
              <div className="col-12">
                <div className="text-muted small">{direction === 'in' ? 'Адрес получателя (наш счёт)' : 'Адрес отправителя (наш счёт)'}</div>
                <code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>{txn.accountId || '—'}</code>
              </div>
              {(txn as any).counterpartyAccount && (
                <div className="col-12">
                  <div className="text-muted small">{direction === 'in' ? 'Адрес отправителя' : 'Адрес получателя'}</div>
                  <code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>{(txn as any).counterpartyAccount}</code>
                </div>
              )}
              {(txn as any).scoringRequestId && (
                <div className="col-12">
                  <div className="text-muted small">Scoring request ID</div>
                  <code style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{(txn as any).scoringRequestId}</code>
                </div>
              )}
            </div>

            {txn.counterpartyName && (
              <div className="border rounded p-2 mb-3">
                <div className="text-muted small mb-1">Контрагент</div>
                <div className="fw-semibold">{txn.counterpartyName}</div>
                {txn.counterpartyAccount && (
                  <code style={{ fontSize: '0.78rem' }}>{txn.counterpartyAccount}</code>
                )}
              </div>
            )}

            {txn.description && (
              <div className="mb-3">
                <div className="text-muted small">Назначение</div>
                <div>{txn.description}</div>
              </div>
            )}
          </div>

          <hr />
          <div className="fw-semibold mb-2">
            Сработавшие правила ({amlCase.triggeredRules.length || amlCase.rulesCount || 0})
          </div>
          <AmlRules rules={amlCase.triggeredRules} />
        </Tab>

        {/* Вкладка 2: Профиль пользователя */}
        <Tab eventKey="profile" title="Профиль">
          <AmlUserProfile amlCase={amlCase} />
        </Tab>

        {/* Вкладка 3: Скор риска */}
        <Tab eventKey="scoring" title="Скор риска" onEntering={handleProviderTabEnter}>
          {providerHistoryLoading ? (
            <div className="d-flex align-items-center gap-2 py-3 text-muted small">
              <Spinner animation="border" size="sm" />
              <span>Загрузка данных от провайдеров…</span>
            </div>
          ) : (
            <AmlScoringPanel breakdown={computedBreakdown} />
          )}
        </Tab>

        {/* Вкладка 4: История */}
        <Tab eventKey="timeline" title="История">
          <AmlTimeline events={amlCase.timeline} />
        </Tab>

        {/* Вкладка 5: История провайдеров (Marble / Crystal) */}
        <Tab
          eventKey="providers"
          title="Провайдеры"
          onEntering={handleProviderTabEnter}
        >
          <AmlProviderHistory
            loading={providerHistoryLoading}
            data={providerHistory}
            error={providerHistoryError}
          />
        </Tab>
      </Tabs>

      {/* Модал решения */}
      <AmlDecisionModal
        show={showDecisionModal}
        amlCase={amlCase}
        onHide={() => setShowDecisionModal(false)}
        onSubmit={onDecisionSubmit}
      />
    </div>
  );
};

export default AmlCaseDetail;
